import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { NgxPermissionsService } from 'ngx-permissions'
import { fromEvent, of, Subscription } from 'rxjs'
import { distinctUntilChanged, finalize, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import * as lodash from 'lodash'
import {
  CalendarEventTimeRange,
  CalendarListComponent,
  CalendarListSelectionInfo,
  CalendarListViewPortInfo,
} from '@tokeet/ngx-calendar-list'

import * as fromRoot from '@tv3/store/state'
import {
  ChannelService,
  Destroyable,
  localTodayToUTC,
  Rental,
  RentalBookedDayTypes,
  RentalService,
  selectAllChannels,
  selectChannelsLoaded,
  timeRange,
  toMoment,
  untilDestroy,
} from '@tokeet/tv3-platform'
import { CalendarService } from '@tv3/store/calendar/calendar.service'
import { selectMultiCalendarEventsForRental } from '@tv3/store/calendar/calendar.selectors'
import { MultiCalendarEventView } from '@tv3/store/calendar/calendar.view'
import { LoadCalendarEventsByRental } from '@tv3/store/calendar/calendar.actions'
import { EditHoldDialogService } from '@tv3/containers/calendar/hold/edit-hold-dialog/edit-hold-dialog.service'
import { BookingDetailsPopupService } from '@tv3/containers/calendar/shared/booking-details-popup/booking-details-popup.service'
import {
  ActionDialogService,
  GetAddEventActionParams,
  GetAddRateActionParams,
} from '@tv3/shared/dialogs/action-dialog/action-dialog.service'
import { AddHoldDialogService } from '@tv3/containers/calendar/hold/add-hold-dialog/add-hold-dialog.service'
import { InquirySharedDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-shared-dialogs.service'
import { EditRateDialogInput } from '../edit-rate-dialog/edit-rate-dialog.component'
import { EditRateDialogService } from '../edit-rate-dialog/edit-rate-dialog.service'
import { RatesService } from './rate-services/rates.service'
import { ExpectPermission } from '@tokeet-frontend/permission'

@Component({
  selector: 'app-rental-rates-calendar',
  templateUrl: './rental-rates-calendar.component.html',
  styleUrls: ['./rental-rates-calendar.component.scss'],
  providers: [RatesService],
})
export class RentalRatesCalendarComponent extends Destroyable implements OnInit {
  @Input() rental: Rental
  @Input() showEvents = false

  @Input() loading = false

  years: number[]
  months: number[]

  selectedMonth: number
  selectedYear: number

  fetchingBlockedDates = true
  blockedDates: Record<string, RentalBookedDayTypes> = {}

  events: MultiCalendarEventView[] = null

  today = localTodayToUTC().unix()

  activePopover: NgbPopover

  calendarList: CalendarListComponent<MultiCalendarEventView>
  scrollSub: Subscription

  constructor(
    private store: Store<fromRoot.State>,
    private permissions: NgxPermissionsService,
    private rentalService: RentalService,
    private ratesService: RatesService,
    private inquirySharedDialogsService: InquirySharedDialogsService,
    private bookingDetailsPopupService: BookingDetailsPopupService,
    private channelService: ChannelService,
    private calendarService: CalendarService,
    private actionDialog: ActionDialogService,
    private editHoldDialog: EditHoldDialogService,
    private addHoldDialog: AddHoldDialogService,
    private editRateDialog: EditRateDialogService
  ) {
    super()
    this.onViewportChange = lodash.debounce(this.onViewportChange, 250)
  }

  ngOnInit() {
    this.buildMonths()
    this.fetchBlockedDates()

    this.store
      .select(selectAllChannels)
      .pipe(
        distinctUntilChanged(),
        withLatestFrom(this.store.select(selectChannelsLoaded)),
        switchMap(([channels, loaded]) => {
          return loaded ? of(channels) : this.channelService.all()
        }),
        tap((channels) => {
          this.ratesService.setChannels(channels)
          this.fetchRates()
        }),
        untilDestroy(this)
      )
      .subscribe()

    if (this.showEvents) {
      this.store
        .select(selectMultiCalendarEventsForRental(this.rental.id))
        .pipe(
          distinctUntilChanged(),
          tap((events) => {
            this.events = events
          }),
          untilDestroy(this)
        )
        .subscribe()

      this.fetchEvents()
    }
  }

  @ViewChild('calendarList', { static: false })
  set setCalendarList(calendarList: CalendarListComponent) {
    this.calendarList = calendarList

    if (this.scrollSub) this.scrollSub.unsubscribe()
    if (!calendarList) return

    this.scrollSub = fromEvent(calendarList.contentScroller.elementRef.nativeElement, 'scroll')
      .pipe(
        tap(() => {
          if (this.activePopover && this.activePopover.isOpen) {
            this.activePopover.close()
          }
        })
      )
      .subscribe()
  }

  get fetching() {
    return this.ratesService.fetching || this.fetchingBlockedDates
  }

  get channels() {
    return this.ratesService.channels || {}
  }

  getEventID = (event: MultiCalendarEventView) => {
    return this.calendarService.getEventID(event)
  }

  getEventTimeRange = (event: MultiCalendarEventView): CalendarEventTimeRange => {
    return this.calendarService.getEventTimeRange(event)
  }

  isStartOfMonth(date: number) {
    return toMoment(date).date() === 1
  }

  isBeforeToday(date: number) {
    return toMoment(date).isBefore(toMoment(), 'date')
  }

  isInYear(month: number, year: number) {
    return toMoment(month).year() === year
  }

  canNavigate(active: number, list: number[], type: 'prev' | 'next') {
    if (type === 'prev') return active > lodash.first(list)
    if (type === 'next') return active < lodash.last(list)
    return false
  }

  getMonthDayData = (date: number) => {
    return this.ratesService.getRate(date)
  }

  refresh(
    options: { rates?: boolean; events?: boolean; blockedDates?: boolean } = {
      rates: true,
      events: true,
      blockedDates: true,
    }
  ) {
    if (options.rates) this.fetchRates(true)
    if (options.events) this.fetchEvents()
    if (options.blockedDates) this.fetchBlockedDates()
  }

  fetchBlockedDates() {
    this.fetchingBlockedDates = true

    this.rentalService
      .getBlockedDates(this.rental)
      .pipe(
        tap((blockedDates) => {
          this.blockedDates = blockedDates
        }),
        finalize(() => {
          this.fetchingBlockedDates = false
        })
      )
      .subscribe()
  }

  fetchRates(refresh = false) {
    this.ratesService.fetchRates(this.rental, refresh).subscribe()
  }

  fetchEvents() {
    if (!this.showEvents) return
    this.store.dispatch(LoadCalendarEventsByRental({ id: this.rental.id }))
  }

  showEventDetails(event: MultiCalendarEventView) {
    if (event.isHoldEvent) {
      this.onEditHold(event)
    } else {
      this.bookingDetailsPopupService.open(event)
    }
  }

  onEditHold(event: MultiCalendarEventView) {
    if (this.permissions.getPermission('canEditHold') && event.isHoldEvent) {
      this.editHoldDialog.open(event, true, true)
    }
  }

  onChangeMonth(type: 'next' | 'prev' = null, scroll = true) {
    const firstMonth = toMoment(lodash.first(this.months))
    const lastMonth = toMoment(lodash.last(this.months))

    let month = toMoment(this.selectedMonth)

    if (type === 'next') {
      month.add(1, 'month')
    } else if (type === 'prev') {
      month.subtract(1, 'month')
    }

    if (month.isBefore(firstMonth, 'month')) {
      month = firstMonth
    } else if (month.isAfter(lastMonth, 'month')) {
      month = lastMonth
    }

    this.selectedMonth = month.startOf('month').unix()
    this.selectedYear = month.year()

    if (scroll) this.calendarList.scrollToDate(this.selectedMonth)
  }

  onChangeYear(type: 'next' | 'prev' = null, scroll = true) {
    if (type === 'next') {
      if (this.selectedYear === lodash.last(this.years)) return
      this.selectedYear += 1
    } else if (type === 'prev') {
      if (this.selectedYear === lodash.first(this.years)) return
      this.selectedYear -= 1
    }

    this.selectedMonth = toMoment()
      .year(this.selectedYear)
      .month(toMoment(this.selectedMonth).month())
      .startOf('month')
      .unix()

    this.onChangeMonth(null, scroll)
  }

  onViewportChange(viewportInfo: CalendarListViewPortInfo) {
    const month = toMoment(viewportInfo.activeMonth)

    this.selectedMonth = month.startOf('month').unix()
    this.selectedYear = month.year()
  }

  @ExpectPermission('readOnly')
  onSelectionChange(selectionInfo: CalendarListSelectionInfo) {
    if (selectionInfo.selectionType === 'click') {
      // TODO: maybe show tooltip
      console.log('onSelectionChange.click', selectionInfo)
      return
    }

    if (selectionInfo.selectionType === 'drag') {
      const dates = selectionInfo.selections

      if (!dates.length) return

      const start = lodash.minBy(dates)
      const end = lodash.maxBy(dates)

      this.showSelectionDialog(start, end)
      return
    }
  }

  private showSelectionDialog(start: number, end: number) {
    const eventActions: GetAddEventActionParams = {
      addBooking: {
        onClick: () => {
          this.inquirySharedDialogsService.openCreateBooking(start, end, this.rental.id)
        },
      },
      addHoldEvent: {
        onClick: () => {
          this.addHoldDialog.openCreateHoldEvent(start, end, this.rental.id)
        },
      },
    }

    const rateActions: GetAddRateActionParams = {
      addStandardRate: {
        onClick: () => this.openRateDialog({ type: 'standard', start, end }),
      },
      addDynamicRate: {
        onClick: () => this.openRateDialog({ type: 'dynamic', start, end }),
      },
    }

    const dialogRef = this.showEvents
      ? this.actionDialog.openRentalCalendarActions({ ...eventActions, ...rateActions })
      : this.actionDialog.openAddRateActions(rateActions)

    dialogRef.beforeClosed().subscribe(() => {
      this.calendarList.clearSelections()
    })
  }

  openRateDialog(data: EditRateDialogInput) {
    const dialogRef = this.editRateDialog.open({
      rentals: [this.rental],
      rental: this.rental,
      ...data,
    })

    dialogRef.beforeClosed().subscribe((result) => {
      this.calendarList.clearSelections()

      if (result && result.rate) this.refresh({ rates: true })
    })
  }

  gotoToday() {
    this.calendarList.scrollToDate(this.today)
  }

  private buildMonths() {
    const start = toMoment().startOf('month')
    const end = start.clone().add(24, 'month')

    this.months = timeRange(start, end, 'month')

    this.years = timeRange(start, end, 'year').map((time) => toMoment(time).year())
    this.selectedYear = lodash.first(this.years)

    this.onChangeYear(null, false)
  }
}
